/**
 * Visit Note Lock Utility
 * Pure functions for computing lock status from scheduledEnd + noteLockHours.
 * No React deps — importable by services and hooks.
 */

// Accept any Firestore Timestamp-like object
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TimestampLike = any

export interface LockCheckParams {
  scheduledEnd: TimestampLike
  noteLockHours: number // 0 = disabled
  unlockOverride?: {
    expiresAt: TimestampLike
  } | null
}

export interface LockStatus {
  isLocked: boolean
  lockTime: Date | null
  timeUntilLock: number | null // ms remaining, null if already locked or disabled
  isTemporarilyUnlocked: boolean
  unlockExpiresAt: Date | null
}

function toDate(ts: TimestampLike | Date): Date {
  if (ts instanceof Date) return ts
  if (typeof ts.toDate === 'function') return ts.toDate()
  return new Date(ts.seconds * 1000)
}

export function getVisitNoteLockStatus(params: LockCheckParams): LockStatus {
  const { scheduledEnd, noteLockHours, unlockOverride } = params

  // Feature disabled
  if (!noteLockHours || noteLockHours <= 0) {
    return {
      isLocked: false,
      lockTime: null,
      timeUntilLock: null,
      isTemporarilyUnlocked: false,
      unlockExpiresAt: null,
    }
  }

  const endDate = toDate(scheduledEnd)
  const lockTime = new Date(endDate.getTime() + noteLockHours * 60 * 60 * 1000)
  const now = new Date()

  // Not yet locked
  if (now < lockTime) {
    return {
      isLocked: false,
      lockTime,
      timeUntilLock: lockTime.getTime() - now.getTime(),
      isTemporarilyUnlocked: false,
      unlockExpiresAt: null,
    }
  }

  // Past lock time — check for temporary unlock override
  if (unlockOverride?.expiresAt) {
    const expiresAt = toDate(unlockOverride.expiresAt)
    if (now < expiresAt) {
      return {
        isLocked: false, // temporarily unlocked
        lockTime,
        timeUntilLock: null,
        isTemporarilyUnlocked: true,
        unlockExpiresAt: expiresAt,
      }
    }
  }

  // Locked
  return {
    isLocked: true,
    lockTime,
    timeUntilLock: null,
    isTemporarilyUnlocked: false,
    unlockExpiresAt: null,
  }
}

export function formatTimeRemaining(ms: number): string {
  if (ms <= 0) return '0m'
  const hours = Math.floor(ms / (1000 * 60 * 60))
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60))
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}
